<template>
  <div class="min-w-0 space-y-5">
    <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div class="flex flex-wrap items-center gap-2">
        <span class="panel-glass-chip inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold">
          <span class="relative flex size-2 shrink-0 overflow-hidden rounded-full">
            <span class="absolute inline-flex size-full animate-ping rounded-full bg-sky-400 opacity-60" />
            <span class="relative inline-flex size-2 rounded-full bg-sky-500" />
          </span>
          {{ __('Daily session calendar') }}
        </span>
        <span class="text-xs text-zinc-500 dark:text-zinc-400">
          {{ __('Max :n opens / Tehran day', { n: data?.daily_limit || 4 }) }}
        </span>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <button
          type="button"
          class="panel-glass-btn rounded-full px-3 py-1.5 text-xs font-semibold"
          :disabled="loading"
          @click="goToday"
        >
          {{ __('Today') }}
        </button>
        <button
          type="button"
          class="panel-glass-btn rounded-full px-3 py-1.5 text-xs font-semibold"
          :disabled="loading"
          @click="fetchMonth({ silent: false })"
        >
          {{ loading ? __('Refreshing…') : __('Refresh') }}
        </button>
        <span v-if="updatedLabel" class="text-xs text-zinc-500 dark:text-zinc-400">
          {{ updatedLabel }}
        </span>
      </div>
    </div>

    <div v-if="error" class="panel-glass rounded-[2rem] p-4 text-sm text-rose-500">
      <div class="panel-glass-content">{{ error }}</div>
    </div>

    <div class="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(20rem,0.85fr)]">
      <article class="panel-glass overflow-hidden rounded-[2rem]">
        <div class="panel-glass-content">
          <div class="flex items-center justify-between gap-3 border-b border-zinc-200/60 px-4 py-3 dark:border-zinc-700/60">
            <button
              type="button"
              class="rounded-full px-3 py-1.5 text-sm font-semibold text-zinc-600 hover:bg-zinc-100/80 dark:text-zinc-300 dark:hover:bg-zinc-800/70"
              :disabled="loading"
              @click="shiftMonth(-1)"
            >
              ‹
            </button>
            <div class="text-center">
              <p class="text-sm font-semibold text-zinc-900 dark:text-white">{{ data?.month_label || '—' }}</p>
              <p class="text-[11px] text-zinc-500 dark:text-zinc-400">{{ monthTotalsLine }}</p>
            </div>
            <button
              type="button"
              class="rounded-full px-3 py-1.5 text-sm font-semibold text-zinc-600 hover:bg-zinc-100/80 dark:text-zinc-300 dark:hover:bg-zinc-800/70"
              :disabled="loading"
              @click="shiftMonth(1)"
            >
              ›
            </button>
          </div>

          <div class="grid grid-cols-7 gap-1 px-2 pt-3 text-center text-[11px] font-semibold text-zinc-400 sm:px-3">
            <div v-for="(label, idx) in (data?.weekdays || [])" :key="'w-' + idx" class="py-1">
              {{ label }}
            </div>
          </div>

          <div class="grid grid-cols-7 items-stretch gap-1 p-2 sm:p-3">
            <div v-for="n in padCount" :key="'pad-' + n" />
            <button
              v-for="day in days"
              :key="day.date"
              type="button"
              class="flex h-full min-h-[4.25rem] min-w-0 flex-col items-stretch justify-start overflow-hidden rounded-xl border px-1 py-1 text-start transition sm:min-h-[4.75rem] sm:rounded-2xl sm:px-1.5 sm:py-1.5"
              :class="dayCellClass(day)"
              :disabled="day.is_future"
              @click="selectDay(day.date)"
            >
              <div class="flex items-start justify-between gap-1">
                <span class="text-xs font-semibold sm:text-sm" :class="day.is_today ? 'text-sky-600 dark:text-sky-300' : ''">
                  {{ day.jalali_day }}
                </span>
                <span v-if="day.opened" class="hidden text-[10px] font-bold tabular-nums sm:inline" :class="pnlClass(day.pnl)">
                  {{ day.opened }}/{{ day.limit }}
                </span>
              </div>
              <div v-if="day.opened" class="mt-1 flex flex-wrap gap-0.5">
                <span
                  v-for="(mark, idx) in day.marks"
                  :key="day.date + '-' + idx"
                  class="text-[11px] leading-none"
                  :class="markClass(mark)"
                >{{ markGlyph(mark) }}</span>
              </div>
              <p v-if="day.opened" class="mt-1 break-all text-[9px] font-semibold leading-tight tabular-nums sm:text-[10px]" :class="pnlClass(day.pnl)">
                {{ formatPnl(day.pnl) }}
              </p>
              <p v-else-if="!day.is_future" class="mt-1 text-[10px] text-zinc-400 sm:mt-2">—</p>
            </button>
          </div>
        </div>
      </article>

      <article class="panel-glass overflow-hidden rounded-[2rem]">
        <div class="panel-glass-content">
          <div class="flex items-center justify-between gap-2 border-b border-zinc-200/60 px-4 py-3 dark:border-zinc-700/60">
            <button
              type="button"
              class="rounded-full px-2.5 py-1 text-sm font-semibold text-zinc-600 hover:bg-zinc-100/80 dark:text-zinc-300 dark:hover:bg-zinc-800/70"
              :disabled="loading || !selected?.prev_date"
              @click="selectDay(selected.prev_date)"
            >
              ‹
            </button>
            <div class="min-w-0 text-center">
              <p class="text-sm font-semibold text-zinc-900 dark:text-white">
                {{ selected?.weekday }} {{ selected?.jalali_label }}
              </p>
              <p class="text-[11px] text-zinc-500 dark:text-zinc-400">{{ selected?.date }}</p>
            </div>
            <button
              type="button"
              class="rounded-full px-2.5 py-1 text-sm font-semibold text-zinc-600 hover:bg-zinc-100/80 dark:text-zinc-300 dark:hover:bg-zinc-800/70"
              :disabled="loading || !selected?.next_date || selectedNextIsFuture"
              @click="selectDay(selected.next_date)"
            >
              ›
            </button>
          </div>

          <div v-if="selected" class="space-y-3 p-4">
            <div class="grid grid-cols-2 gap-2">
              <div class="rounded-2xl bg-zinc-100/70 px-3 py-2 dark:bg-zinc-900/50">
                <p class="text-[10px] text-zinc-500 dark:text-zinc-400">{{ __('Opened') }}</p>
                <p class="mt-0.5 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                  {{ selected.opened }} / {{ selected.limit }}
                </p>
              </div>
              <div class="rounded-2xl bg-zinc-100/70 px-3 py-2 dark:bg-zinc-900/50">
                <p class="text-[10px] text-zinc-500 dark:text-zinc-400">{{ __('Result') }}</p>
                <p class="mt-0.5 text-sm font-semibold" :class="pnlClass(selected.pnl)">
                  {{ resultLabel(selected) }}
                </p>
              </div>
              <div class="rounded-2xl bg-zinc-100/70 px-3 py-2 dark:bg-zinc-900/50">
                <p class="text-[10px] text-zinc-500 dark:text-zinc-400">{{ __('Wins / losses') }}</p>
                <p class="mt-0.5 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                  {{ __(':wins wins · :losses losses', { wins: selected.profited, losses: selected.stopped }) }}
                </p>
              </div>
              <div class="rounded-2xl bg-zinc-100/70 px-3 py-2 dark:bg-zinc-900/50">
                <p class="text-[10px] text-zinc-500 dark:text-zinc-400">{{ __('Realized PnL') }}</p>
                <p class="mt-0.5 text-sm font-semibold tabular-nums" :class="pnlClass(selected.pnl)">
                  {{ formatPnl(selected.pnl) }}
                </p>
              </div>
            </div>

            <p v-if="selected.locked" class="rounded-2xl bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-700 dark:text-amber-300">
              {{ __('3 consecutive stops — session locked that night.') }}
            </p>

            <div class="flex flex-wrap gap-1">
              <span
                v-for="(mark, idx) in selected.marks"
                :key="'sel-' + idx"
                class="inline-flex size-7 items-center justify-center rounded-lg text-sm font-bold"
                :class="markBadgeClass(mark)"
              >{{ markGlyph(mark) }}</span>
              <span v-if="!selected.marks.length" class="text-xs text-zinc-400">
                {{ __('No trades opened this day.') }}
              </span>
            </div>

            <div class="divide-y divide-zinc-100 dark:divide-zinc-800">
              <a
                v-for="trade in selected.trades"
                :key="trade.id"
                :href="trade.url"
                class="flex items-center justify-between gap-3 py-2.5 hover:opacity-80"
              >
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-zinc-900 dark:text-white">
                    {{ trade.coin }}
                    <span :class="trade.side === 'long' ? 'text-emerald-500' : 'text-rose-400'">
                      {{ trade.side_label }}
                    </span>
                  </p>
                  <p class="text-[11px] text-zinc-500 dark:text-zinc-400">
                    {{ trade.opened_at || '—' }}
                    <span v-if="trade.closed_at"> → {{ trade.closed_at }}</span>
                    · {{ trade.entry_label }}
                    · {{ trade.status_label }}
                  </p>
                </div>
                <div class="shrink-0 text-end">
                  <p class="text-sm font-bold" :class="markClass(trade.mark)">{{ markGlyph(trade.mark) }}</p>
                  <p class="text-[11px] font-semibold tabular-nums" :class="pnlClass(trade.pnl)">
                    {{ formatPnl(trade.pnl_is_percent ? trade.pnl_pct : trade.pnl, trade.pnl_is_percent) }}
                  </p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
export default {
  props: ['card'],

  data() {
    return {
      data: null,
      loading: false,
      error: '',
      month: null,
      day: null,
      timer: null,
      onLiveUpdate: null,
    }
  },

  computed: {
    days() {
      return this.data?.days || []
    },

    selected() {
      return this.data?.selected || null
    },

    padCount() {
      return Math.max(0, Number(this.data?.offset || 0))
    },

    selectedNextIsFuture() {
      const next = this.selected?.next_date
      const today = this.data?.today
      return !next || !today || next > today
    },

    monthTotalsLine() {
      const days = this.days.filter(day => day.opened > 0)
      if (!days.length) return this.__('No opened trades this month.')
      const opened = days.reduce((sum, day) => sum + day.opened, 0)
      const wins = days.reduce((sum, day) => sum + day.profited, 0)
      const losses = days.reduce((sum, day) => sum + day.stopped, 0)
      const pnl = days.reduce((sum, day) => sum + Number(day.pnl || 0), 0)
      return this.__(':opened opens · :wins wins · :losses losses · :pnl', {
        opened,
        wins,
        losses,
        pnl: this.formatPnl(pnl),
      })
    },

    updatedLabel() {
      const at = this.data?.generated_at
      if (!at) return ''
      try {
        return new Date(at).toLocaleTimeString('fa-IR', {
          timeZone: 'Asia/Tehran',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
      } catch {
        return ''
      }
    },
  },

  mounted() {
    this.fetchMonth()
    this.timer = setInterval(() => this.fetchMonth({ silent: true }), 60000)

    if (typeof Nova !== 'undefined' && Nova.$on) {
      this.onLiveUpdate = payload => {
        if (!payload?.resources?.includes('main-points')) return
        if (payload.action === 'prices') return
        this.fetchMonth({ silent: true })
      }
      Nova.$on('nova-live', this.onLiveUpdate)
    }
  },

  beforeUnmount() {
    clearInterval(this.timer)
    if (typeof Nova !== 'undefined' && Nova.$off && this.onLiveUpdate) {
      Nova.$off('nova-live', this.onLiveUpdate)
    }
  },

  methods: {
    goToday() {
      this.month = null
      this.day = null
      this.fetchMonth()
    },

    shiftMonth(delta) {
      if (!this.data) return
      this.month = delta < 0 ? this.data.prev_month : this.data.next_month
      this.day = null
      this.fetchMonth()
    },

    selectDay(date) {
      if (!date) return
      this.day = date
      this.fetchMonth()
    },

    async fetchMonth(options = {}) {
      const silent = options.silent === true
      if (!silent) this.loading = true
      this.error = ''

      try {
        const params = {}
        if (this.month) params.month = this.month
        if (this.day) params.day = this.day
        const { data } = await Nova.request().get('/nova-api/daily-session-calendar', { params })
        this.data = data
        this.month = data.month
        this.day = data.selected?.date || this.day
      } catch (e) {
        if (!silent) {
          this.error = e?.response?.data?.message || this.__('Failed to load daily session calendar.')
        }
      } finally {
        if (!silent) this.loading = false
      }
    },

    dayCellClass(day) {
      const selected = this.selected?.date === day.date
      if (day.is_future) {
        return 'cursor-default border-transparent text-zinc-300 dark:text-zinc-700'
      }
      if (selected) {
        return 'border-sky-400/80 bg-sky-500/15 shadow-sm dark:border-sky-500/60'
      }
      if (day.result === 'win') {
        return 'border-emerald-200/80 bg-emerald-50/80 hover:border-emerald-400 dark:border-emerald-900/60 dark:bg-emerald-950/30'
      }
      if (day.result === 'loss') {
        return 'border-rose-200/80 bg-rose-50/80 hover:border-rose-400 dark:border-rose-900/60 dark:bg-rose-950/30'
      }
      if (day.result === 'mixed' || day.result === 'open') {
        return 'border-amber-200/80 bg-amber-50/70 hover:border-amber-400 dark:border-amber-900/50 dark:bg-amber-950/20'
      }
      if (day.is_today) {
        return 'border-sky-200/80 bg-sky-50/50 hover:border-sky-400 dark:border-sky-900/50 dark:bg-sky-950/20'
      }
      return 'border-transparent bg-zinc-50/70 hover:border-zinc-300 dark:bg-zinc-900/40 dark:hover:border-zinc-600'
    },

    markGlyph(mark) {
      return { win: '✓', loss: '✗', scratch: '○', open: '●' }[mark] || '·'
    },

    markClass(mark) {
      return {
        win: 'text-emerald-600 dark:text-emerald-400',
        loss: 'text-rose-600 dark:text-rose-400',
        scratch: 'text-zinc-400',
        open: 'text-sky-500',
      }[mark] || 'text-zinc-400'
    },

    markBadgeClass(mark) {
      return {
        win: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
        loss: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
        scratch: 'bg-zinc-500/10 text-zinc-500',
        open: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
      }[mark] || 'bg-zinc-500/10 text-zinc-400'
    },

    pnlClass(value) {
      const n = Number(value || 0)
      if (n > 0) return 'text-emerald-600 dark:text-emerald-400'
      if (n < 0) return 'text-rose-600 dark:text-rose-400'
      return 'text-zinc-500 dark:text-zinc-400'
    },

    formatPnl(value, isPercent) {
      const n = Number(value || 0)
      const sign = n > 0 ? '+' : ''
      if (isPercent) {
        return `${sign}${n.toFixed(2)}%`
      }
      return `${sign}${n.toFixed(2)} USDT`
    },

    resultLabel(day) {
      if (!day.opened) return this.__('No trades')
      if (day.locked) return this.__('Locked after 3 stops')
      if (day.still_open && !day.profited && !day.stopped) return this.__('Still open')
      if (day.stopped && !day.profited) return this.__('Loss day')
      if (day.profited && !day.stopped) return this.__('Win day')
      return this.__('Mixed day')
    },
  },
}
</script>
